import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import type { Scholarship } from '../../../../types/scholarships';
import { SlidersIcon } from './icons';
import {
  availableCards,
  defaultCardsFor,
  resolveVisibleCards,
  storedCards,
  toggleCard,
  type SummaryCardKey,
} from './summaryCards';
import './SummaryCardPicker.css';

interface SummaryCardPickerProps {
  scholarship: Scholarship;
  /** Called with the full new key list. The caller owns persistence and error handling. */
  onChange: (visibleCards: string[]) => void;
}

/**
 * Chooses which summary cards show above the board.
 *
 * Deliberately built in ColumnSettingsPanel's language rather than the design canvas's — same
 * menu chrome, ticks rather than switches, and a quiet reset row at the TOP instead of the
 * canvas's Cancel/Done footer. Two reasons: web/CLAUDE.md names that reset-at-top idiom as the
 * project's popover convention, and a footer with Done implies changes are pending until you
 * press it, when in fact each tick saves immediately.
 *
 * There is no Cancel for the same reason. If undo becomes a real request, the honest fix is a
 * toast with an undo action, not a fake pending state.
 */
export const SummaryCardPicker = ({ scholarship, onChange }: SummaryCardPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setIsOpen(false);
      // Escape must not strand focus on a node that no longer exists, or the next Tab
      // restarts from the top of the document.
      wrapperRef.current?.querySelector('button')?.focus();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const stored = storedCards(scholarship);
  const offered = availableCards(scholarship);
  const shown = new Set<SummaryCardKey>(resolveVisibleCards(stored, scholarship));

  // Count only cards this roster could actually show. A stored key whose tracking is off is
  // not "hidden" in any sense the coach can act on here, so counting it would show a badge
  // pointing at a control that lives in Configure roster.
  const hiddenCount = offered.length - shown.size;

  const isCustomised = offered.some((c) => !shown.has(c.key)) || stored.length === 0;

  const handleToggle = (key: SummaryCardKey, visible: boolean) => {
    onChange(toggleCard(stored, key, visible));
  };

  return (
    <div className="summary-card-picker" ref={wrapperRef}>
      <Button
        variant="secondary"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={hiddenCount > 0 ? `Summary (${hiddenCount} hidden)` : 'Summary'}
        data-testid="summary-card-picker-button"
      >
        <SlidersIcon />
        Summary
        {hiddenCount > 0 && (
          <span className="summary-card-picker__badge" aria-hidden="true">
            {hiddenCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="summary-card-picker__panel" ref={panelRef}>
          <div className="summary-card-picker__header">
            <span className="summary-card-picker__title" id={titleId}>
              Summary cards
            </span>
            <span className="summary-card-picker__hint">
              Choose what shows above the board
            </span>
          </div>

          {/*
            Reset only when there is something to reset, matching ColumnSettingsPanel — a
            permanent row would advertise an action that does nothing on a default band.
          */}
          {isCustomised && (
            <button
              type="button"
              className="summary-card-picker__reset"
              onClick={() => onChange(defaultCardsFor(scholarship))}
            >
              Show all cards
            </button>
          )}

          {/*
            role="group" + aria-labelledby rather than wrapping the list in a <label>: a label
            naming a set of controls is not a label and trips label-has-associated-control
            (see web/CLAUDE.md).
          */}
          <ul
            className="summary-card-picker__list"
            role="group"
            aria-labelledby={titleId}
          >
            {offered.map((card) => (
              <li key={card.key} className="summary-card-picker__row">
                <Checkbox
                  className="summary-card-picker__toggle"
                  checked={shown.has(card.key)}
                  onChange={(e) => handleToggle(card.key, e.target.checked)}
                  label={
                    <span className="summary-card-picker__label-text">{card.label}</span>
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
